// src/utils/teamHelper.js
// Centralized helpers for Team actions & data shaping.
// - Wraps Cloud Functions (Admin-side writes) so YourTeam.js stays lean
// - Enriches team with member profiles
// - Provides small utilities (isCaptain, isMember, currency, etc.)

import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';

/** Region your callables are deployed to */
export const FUNCTIONS_REGION = 'us-central1';

/** Firebase Functions caller */
export function callFn(name, { app } = {}) {
  const fns = getFunctions(app, FUNCTIONS_REGION);
  return httpsCallable(fns, name);
}

/** Normalize Firebase callable errors into a friendly Error with .code */
export function toFriendlyError(err) {
  const code = (err?.code || '').replace('functions/', '') || 'unknown';
  // Many SDK error messages are like "functions/already-exists: Team name is taken"
  // Strip the leading "functions/xyz:" if present
  let message = err?.message || 'Unknown error';
  const idx = message.indexOf(':');
  if (idx > -1 && message.slice(0, idx).includes('/')) message = message.slice(idx + 1).trim();
  const e = new Error(message);
  e.code = code;
  e.raw = err;
  return e;
}

/* =========================
 * Cloud Function wrappers
 * ========================= */
export async function createTeam({ name, tag = null }, opts = {}) {
  try {
    const res = await callFn('teamsCreate', opts)({ name, tag });
    return res?.data; // { id, inviteCode }
  } catch (err) { throw toFriendlyError(err); }
}

export async function joinTeamByCode(inviteCode, opts = {}) {
  try {
    const res = await callFn('teamsJoinByCode', opts)({ inviteCode });
    return res?.data; // { teamId }
  } catch (err) { throw toFriendlyError(err); }
}

export async function leaveTeam(teamId, opts = {}) {
  try {
    const res = await callFn('teamsLeave', opts)({ teamId });
    return res?.data; // { ok, alreadyLeft? }
  } catch (err) { throw toFriendlyError(err); }
}

export async function transferCaptain(teamId, newOwnerUid, opts = {}) {
  try {
    const res = await callFn('teamsTransfer', opts)({ teamId, newOwnerUid });
    return res?.data; // { ok: true }
  } catch (err) { throw toFriendlyError(err); }
}

export async function disbandTeam(teamId, { graceful = true } = {}, opts = {}) {
  try {
    const res = await callFn('teamsDisband', opts)({ teamId, graceful });
    return res?.data; // { ok: true }
  } catch (err) { throw toFriendlyError(err); }
}

export async function saveTeam(teamId, opts = {}) {
  try {
    // teamsCancelDisband: any-member "save the team" during the 72h disband
    // grace window. (This used to call teamsSave — the captain-only SETTINGS
    // editor — so non-captain saves died with permission-denied and no save
    // ever cleared the disband flags.)
    const res = await callFn('teamsCancelDisband', opts)({ teamId });
    return res?.data; // { ok: true, saved: true }
  } catch (err) { throw toFriendlyError(err); }
}

export async function regenerateInvite(teamId, opts = {}) {
  try {
    const res = await callFn('teamsRegenerateInvite', opts)({ teamId });
    return res?.data; // { inviteCode }
  } catch (err) { throw toFriendlyError(err); }
}

export async function removeMember(teamId, memberUid, opts = {}) {
  try {
    const res = await callFn('teamsRemoveMember', opts)({ teamId, memberUid });
    return res?.data; // { ok, alreadyRemoved? }
  } catch (err) { throw toFriendlyError(err); }
}

export async function getMyTeam(opts = {}) {
  try {
    const res = await callFn('teamsGetMine', opts)({});
    return res?.data; // { team: {...} | null }
  } catch (err) { throw toFriendlyError(err); }
}

/** Preferred: overview (team + stats + nextEvents). Falls back to getMyTeam. */
export async function getMyTeamOverview(opts = {}) {
  try {
    const res = await callFn('teamsGetOverview', opts)({});
    return res?.data; // { team, stats?, nextEvents? }
  } catch (err) {
    // Fallback if overview not deployed
    if ((err?.code || '').includes('not-found')) return await getMyTeam(opts);
    throw toFriendlyError(err);
  }
}

/* =========================
 * Data enrichment utilities
 * ========================= */

/** Load a member's public profile; avoids /users restricted reads. */
export async function loadPublicProfile(db, uid) {
  try {
    const snap = await getDoc(doc(db, 'public_profiles', uid));
    if (snap.exists()) {
      const d = snap.data();
      return { uid, userName: d?.userName || d?.displayName || d?.gamertag || uid, photoURL: d?.profilePhotoUrl || null };
    }
  } catch (_) {}
  return { uid, userName: uid, photoURL: null };
}

/** Attach memberProfiles to a team object. */
export async function enrichTeamWithProfiles(db, team) {
  if (!team) return null;
  const ids = Array.isArray(team.memberIds) ? team.memberIds : (team.ownerId ? [team.ownerId] : []);
  const memberProfiles = await Promise.all(ids.map(uid => loadPublicProfile(db, uid)));
  return { ...team, memberProfiles };
}

/** Try overview → fallback to basic team; always returns { team, stats, nextEvents } enriched with profiles. */
export async function loadTeamBundle(db, opts = {}) {
  const payload = await getMyTeamOverview(opts);
  const team = payload?.team || null;
  if (!team) return { team: null, stats: defaultStats(), nextEvents: [] };

  const enriched = await enrichTeamWithProfiles(db, team);
  return {
    team: enriched,
    stats: payload?.stats || defaultStats(enriched),
    nextEvents: payload?.nextEvents || [],
  };
}

/* =========================
 * Small helpers
 * ========================= */
export function defaultStats(seed) {
  // You can compute derived values from seed later if you decide to store aggregates on the team doc
  return { wins: 0, earningsCents: 0, eventsPlayed: 0, winRate: 0, streak: 0, ...(seed?.stats || {}) };
}



export function isMember(team, uid) {
  return !!team && Array.isArray(team.memberIds) && team.memberIds.includes(uid);
}

export function usd(cents = 0) {
  return (Number(cents || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/** Defensive: extract milliseconds from various timestamp shapes */
export function tsToMs(ts) {
  if (!ts) return null;
  if (typeof ts === 'number') return ts;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  const d = new Date(ts);
  const ms = d?.getTime?.();
  return Number.isFinite(ms) ? ms : null;
}



/** Team Captain Helper to load team members into an event */
export function isCaptain(team, uid) {
  return !!team && team.ownerId === uid;
}

export async function loadCaptainTeam(db, uid) {
  if (!uid) return null;
  try {
    const q = query(
      collection(db, 'teams'),
      where('ownerId', '==', uid),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const teamDoc = snap.docs[0];
    const teamData = { id: teamDoc.id, ...teamDoc.data() };
    return await enrichTeamWithProfiles(db, teamData);
  } catch (e) {
    console.error('Error loading captain team:', e);
    return null;
  }
}

export function pickDefaultRoster(team, teamSize) {
  if (!team) return [];
  const captain = team.memberProfiles.find(p => p.uid === team.ownerId);
  const others = team.memberProfiles.filter(p => p.uid !== team.ownerId);
  return [captain, ...others].slice(0, Math.max(1, Number(teamSize || 1)));
}